import * as Joi from "joi";
import {
	Column,
	Entity,
	JoinColumn,
	JoinTable,
	ManyToMany,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { GenPartial } from 'typeormgen';
import { EpisodicItemESDoc } from './episodic-item.interfaces';
import ChangeEpisodicItem from '../change-episodic-items/change-episodic-item.entity';
import CmCategory from '../cm-categories/cm-category.entity';
import CmEpisodicItemPicture from '../cm-episodic-item-pictures/cm-episodic-item-picture.entity';
import CmStatus from '../cm-statuses/cm-status.entity';
import CmStorageLocation from '../cm-storage-locations/cm-storage-location.entity';
import CmWrapBoxEpisodicItem from '../cm-wrap-box-episodic-items/cm-wrap-box-episodic-item.entity';
import CmWrapBox from '../cm-wrap-boxes/cm-wrap-box.entity';
import { DEPARTMENTS } from '../enums/enums';
import EpisodicCharacter from '../episodic-characters/episodic-character.entity';
import Episodic from '../episodics/episodic.entity';
import { Indexable } from '../indexable';
import Inventory from '../inventory/inventory.entity';
import MoneyTrackingPayment from '../money-tracking-payments/money-tracking-payment.entity';
import Vendor from '../vendors/vendor.entity';

@Entity('episodic_items')
export default class EpisodicItem
	extends Inventory
	implements Indexable<EpisodicItemESDoc> {
	static tableName = 'episodic_items';

	static deptId = DEPARTMENTS.CM;

	static columns = [
		...Inventory.columns,
		'wrap_quantity',
		'episodic_character_id',
	];

	@Column('int')
	episodic_character_id: number;

	@Column('int')
	series_item_id: number;

	@Column('int')
	cm_wrap_box_id: number;

	@Column('varchar')
	wrap_quantity: string;

	@Column('int')
	type_name: number;

	@OneToMany(
		(type) => ChangeEpisodicItem,
		(changeEpisodicItem) => changeEpisodicItem.episodicItem
	)
	changeEpisodicItems: ChangeEpisodicItem[];

	@OneToMany(
		(type) => CmEpisodicItemPicture,
		(cmEpisodicItemPicture) => cmEpisodicItemPicture.episodicItem
	)
	cmEpisodicItemPictures: CmEpisodicItemPicture[];

	@ManyToOne((type) => Episodic, (episodic) => episodic.episodicItems)
	@JoinColumn({
		name: 'episodic_id',
	})
	episodic: Episodic;

	@ManyToOne((type) => CmCategory, (category) => category.episodicItems)
	@JoinColumn({
		name: 'category_id',
	})
	category: CmCategory;

	@ManyToOne((type) => CmStatus, (status) => status.episodicItems)
	@JoinColumn({
		name: 'status_id',
	})
	status: CmStatus;

	@ManyToOne((type) => Vendor, (vendor) => vendor.episodicItems)
	@JoinColumn({
		name: 'vendor_id',
	})
	vendor: Vendor;

	readonly department = EpisodicItem.deptId;

	@ManyToOne(
		(type) => CmStorageLocation,
		(cmStorageLocation) => cmStorageLocation.episodicItems
	)
	@JoinColumn({
		name: 'storage_location_id',
	})
	storageLocation: CmStorageLocation;

	@ManyToOne(
		(type) => EpisodicCharacter,
		(episodicCharacter) => episodicCharacter.episodicItems
	)
	@JoinColumn({
		name: 'episodic_character_id',
	})
	episodicCharacter: EpisodicCharacter;

	@OneToOne(
		(type) => CmWrapBoxEpisodicItem,
		(cmWrapBoxEpisodicItem) => cmWrapBoxEpisodicItem.episodicItem
	)
	cmWrapBoxEpisodicItem: CmWrapBoxEpisodicItem;

	@ManyToMany(() => CmWrapBox, (cmWrapBox) => cmWrapBox.episodicItems)
	@JoinTable({
		name: 'cm_wrap_box_episodic_items',
		joinColumn: { name: 'episodic_item_id' },
		inverseJoinColumn: { name: 'cm_wrap_box_id' },
	})
	cmWrapBoxes: CmWrapBox[];

	@ManyToMany(
		() => MoneyTrackingPayment,
		(moneyTrackingPayment) => moneyTrackingPayment.episodicItems
	)
	@JoinTable({
		name: 'episodic_item_payments',
		joinColumn: { name: 'episodic_item_id' },
		inverseJoinColumn: { name: 'money_tracking_payment_id' },
	})
	moneyTrackingPayments: MoneyTrackingPayment[];

	static rules() {
		return {
			...Inventory.rules(),
			episodic_character_id: Joi.number().integer().allow(null),
			series_item_id: Joi.number().integer().allow(null),
			cm_wrap_box_id: Joi.number().integer().allow(null),
			wrap_quantity: Joi.string().max(1000).allow(null, ''),
			episodicCharacter: Joi.object(EpisodicCharacter.rules()).allow(null),
			cmEpisodicItemPictures: Joi
				.array()
				.items(CmEpisodicItemPicture.rules())
				.allow(null),
		};
	}

	constructor(props?: GenPartial<EpisodicItem>) {
		super(props);
	}

	toJSON() {
		return {
			...super.toJSON(),
			episodic_character_id: this.episodic_character_id,
			series_item_id: this.series_item_id,
			cm_wrap_box_id: this.cm_wrap_box_id,
			department: this.department,
			wrap_quantity: this.wrap_quantity,
			episodicCharacter: this.episodicCharacter,
			cmEpisodicItemPictures: this.cmEpisodicItemPictures
				? this.cmEpisodicItemPictures
				: undefined,
			main_photo_id: this.main_photo_id
				? this.main_photo_id
				: this.cmEpisodicItemPictures && this.cmEpisodicItemPictures.length > 0
				? this.cmEpisodicItemPictures.find((pic) => pic.main_photo === true)
					? this.cmEpisodicItemPictures.find((pic) => pic.main_photo === true)
							.picture_id
					: undefined
				: undefined,
			cmWrapBoxes: this.cmWrapBoxes,
			wrap_box_id: this.wrap_box_id,
			inv_wrap_box_names: this.inv_wrap_box_names
				? this.inv_wrap_box_names
				: this.cmWrapBoxes && this.cmWrapBoxes.length > 0
				? this.getWrapBoxNames(this.cmWrapBoxes)
				: undefined,
			storageLocation: this.storageLocation,
			storage_location_name: this.storage_location_name
				? this.storage_location_name
				: this.storageLocation
				? this.storageLocation.name
				: undefined,
			category: this.category,
			category_name: this.category_name
				? this.category_name
				: this.category
				? this.category.name
				: undefined,
			status: this.status,
			status_name: this.status_name
				? this.status_name
				: this.status
				? this.status.name
				: undefined,
			vendor: this.vendor,
			vendor_name: this.vendor_name
				? this.vendor_name
				: this.vendor
				? this.vendor.name
				: undefined,
		};
	}

	toESDoc(data?: { episodicCharacter: EpisodicCharacter }) {
		let char_first_name: string, char_last_name: string, char_num: string;

		if (data) {
			const { episodicCharacter } = data;
			if (episodicCharacter === null) {
				char_first_name = null;
				char_last_name = null;
				char_num = null;
			} else {
				char_first_name = episodicCharacter && episodicCharacter.first_name;
				char_last_name = episodicCharacter && episodicCharacter.last_name;
				char_num = episodicCharacter && episodicCharacter.char_num;
			}
		}

		return {
			id: this.id,
			episodic_id: this.episodic_id,
			name: this.name,
			brand: this.brand,
			color: this.color,
			description: this.description,
			asset_num: this.asset_num,
			disposition_notes: this.disposition_notes,
			size: this.size,
			char_first_name,
			char_last_name,
			ep_char_id: this.episodic_character_id,
			char_num,
		};
	}
}
